import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'ui-library';
import { getSetupData } from 'additiv-services/setup';
import { portfoliosColumns } from '../../constants/constants';
import Card from '../Card/Card';
import EmptyContent from '../EmptyContent';
import Preloader from '../Preloader';
import { adaptRemoveLinkPortfolio } from './adapters/adaptRemoveLinkPortfolio';
import './Portfolios.css';

// 1. Define the Portfolio Item interface
interface PortfolioItem {
    Name: {
        label: string;
        link: string;
    };
    Product: string;
    Profile: string;
    Value: string;
    Performance: number | string;
    Icon?: string;
}

// 2. Define the grouped data structure
interface PortfoliosData {
    jointRegularPortfolios?: PortfolioItem[];
    poaPortfolios?: PortfolioItem[];
    parentalPortfolios?: PortfolioItem[];
}

type ErrorType = { type?: string | undefined; message: string; }

// 3. Define Component Props
interface PortfoliosProps {
    data?: PortfoliosData;
    isLoading?: boolean;
    error?: ErrorType | string | null | undefined;
    onOpenNewPortfolio?: () => void;
    onOpenNewProposal?: () => void;
    link?: string;
    pageSize?: number;
    expiredClientName?: string;
}

const setupData = getSetupData();

const Portfolios: React.FC<PortfoliosProps> = ({
    data = {},
    isLoading = true,
    error = null,
    onOpenNewPortfolio = () => {},
    onOpenNewProposal = () => {},
    link = '',
    pageSize = 9,
    expiredClientName,
}) => {
    const { t } = useTranslation();

    // Helper to avoid repeating Table configuration
    const renderTable = (items: PortfolioItem[] | undefined) => (
        <Table
            data={adaptRemoveLinkPortfolio(items || [], expiredClientName)}
            columns={portfoliosColumns(t)}
            paging={{ pageSize }}
            headerControls={{
                selectControlsLabel: {
                    selectAll: t('headerControls.selectAll'),
                    reset: t('headerControls.reset'),
                },
            }}
        />
    );

    return (
        <Card
            title={t('clientDashboard.portfolios.plan')}
            titleLink={link}
            headerActions={(
                <div className="flex gap-20">
                    {setupData?.PRODUCT_PROPOSAL_FEATURE_FLAG && (
                        <button
                            data-cy="onOpenNewProposal"
                            type="button"
                            className="button button-small button-primary"
                            onClick={onOpenNewProposal}
                        >
                            {t('clientDashboard.portfolios.createProposal')}
                        </button>
                    )}
                    <button
                        data-cy="onOpenNewPortfolio"
                        type="button"
                        className="button button-small button-primary"
                        onClick={onOpenNewPortfolio}
                    >
                        {t('clientDashboard.portfolios.openNewPortfolio')}
                    </button>
                </div>
            )}
        >
            <Preloader isLoading={isLoading} error={error as ErrorType}>
                <EmptyContent data={data} text={t('clientDashboard.portfolios.noPortfolios')}>
                    {data?.jointRegularPortfolios && data.jointRegularPortfolios.length > 0 && (
                        <>
                            <h3>{t('clientDashboard.portfolios.myAccounts')}</h3>
                            {renderTable(data.jointRegularPortfolios)}
                        </>
                    )}

                    {data?.poaPortfolios && data.poaPortfolios.length > 0 && (
                        <>
                            <h3>{t('clientDashboard.portfolios.powerOfAttorney')}</h3>
                            {renderTable(data.poaPortfolios)}
                        </>
                    )}

                    {data?.parentalPortfolios && data.parentalPortfolios.length > 0 && (
                        <>
                            <h3>{t('clientDashboard.portfolios.parentalAuthority')}</h3>
                            {renderTable(data.parentalPortfolios)}
                        </>
                    )}
                </EmptyContent>
            </Preloader>
        </Card>
    );
};

export default Portfolios;
